import {QueryFunctionContext, useQuery} from 'react-query';
import api from 'api';
import {TrackingDetails, TrackingStatus} from 'types';
import {format, parse} from 'date-fns';

// Banner-only statuses (show only StatusBanner)
const BANNER_ONLY_STATUSES: TrackingStatus[] = [
  'completed',
  'billed',
  'filled',
  'otc_status',
  'waiting_for_pickup',
  'ready_for_delivery',
  'delivery_partner',
  'cancel_script',
  'archive',
  'redispatch',
];

// Map statuses (show map + full UI)
const MAP_STATUSES: TrackingStatus[] = ['delivering'];

// Full UI statuses (no map, but show order details)
const FULL_UI_STATUSES: TrackingStatus[] = ['picked_up', 'delivered'];

const getTrackingDetails = async ({queryKey}: QueryFunctionContext) => {
  const [, trackingId] = queryKey;
  const {data} = await api.get<TrackingDetails>(
    `/requests/track/${trackingId}/`
  );
  return data;
};

export const getStatusLabel = (status: TrackingStatus): string => {
  const labels: Record<TrackingStatus, string> = {
    completed: 'Order Completed',
    billed: 'Order Billed',
    filled: 'Order Filled',
    otc_status: 'OTC Order',
    waiting_for_pickup: 'Ready for Pickup',
    ready_for_delivery: 'Ready for Delivery',
    delivering: 'Delivering',
    delivery_partner: 'Third-Party Delivery',
    picked_up: 'Picked Up',
    delivered: 'Delivered',
    cancel_script: 'Script Canceled',
    archive: 'Order Archived',
    redispatch: 'Redelivery Scheduled',
  };
  return labels[status];
};

export const useTracking = ({trackingId}: {trackingId: string}) => {
  const {data: trackingDetails, isLoading} = useQuery(
    ['tracking', trackingId],
    getTrackingDetails,
    {
      enabled: !!trackingId,
      refetchOnWindowFocus: true,
    }
  );

  const status = trackingDetails?.status;

  const shouldShowBannerOnly = status
    ? BANNER_ONLY_STATUSES.includes(status)
    : false;

  const shouldShowMap = status ? MAP_STATUSES.includes(status) : false;

  const shouldShowFullUI = status
    ? MAP_STATUSES.includes(status) || FULL_UI_STATUSES.includes(status)
    : false;

  const driverName = trackingDetails?.route?.driver?.full_name ?? '';

  const rawEta = trackingDetails?.delivery?.arrival_time ?? '';
  const eta =
    rawEta && typeof rawEta === 'string'
      ? format(parse(rawEta, 'HH:mm:ss', new Date()), 'hh:mm a')
      : '';

  const stopsBeforeCount =
    trackingDetails?.delivery?.delivery_stops_before ?? 0;

  const deliveryAddress = trackingDetails?.delivery_address;

  const formattedAddress = deliveryAddress
    ? `${deliveryAddress.address_line_1}${
        deliveryAddress.unit_address ? `, ${deliveryAddress.unit_address}` : ''
      }, ${deliveryAddress.city}, ${deliveryAddress.state} ${
        deliveryAddress.zipcode
      }`
    : '';

  return {
    trackingDetails,
    isLoading,
    status,
    shouldShowBannerOnly,
    shouldShowMap,
    shouldShowFullUI,
    driverName,
    eta,
    stopsBeforeCount,
    deliveryAddress,
    formattedAddress,
  };
};
